import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays } from 'date-fns';
import superagent from 'superagent';
import { backendUrl } from '@helpers/consts';

import { Download, AlertTriangle, Package } from 'lucide-react';

import { Button } from "@shadcn/components/ui/button";
import { Card, CardContent, CardDescription,  CardHeader, CardTitle } from "@shadcn/components/ui/card";

import { Label } from "@shadcn/components/ui/label";

import { Badge } from "@shadcn/components/ui/badge";

import { Table, TableBody,  TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@shadcn/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shadcn/components/ui/tooltip";

import {  toast } from "react-toastify";

import { Skeleton } from "@shadcn/components/ui/skeleton";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  ChartTooltip,
  ChartLegend,
  Title
);


const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d',
  '#a4de6c', '#d0ed57', '#ffc658', '#ff7300', '#8dd1e1', '#e4d354',
  '#fc8d59', '#b2df8a', '#cab2d6', '#6a3d9a', '#b15928', '#ffffb3'
];

const MispickReportPage = () => {
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const [selectedCountries, setSelectedCountries] = useState([]);

  
  const summaryData = React.useMemo(() => {
    if (!filteredData.length) return {
      totalOrders: 0,
      totalBackordered: 0,
      averageMisPickRate: 0,
      highestMisPickRate: { country: '-', rate: 0 }
    };

    const totalOrders = filteredData.reduce((sum, item) => sum + item.totalOrders, 0);
    const totalBackordered = filteredData.reduce((sum, item) => sum + item.backordered, 0);
    const averageMisPickRate = totalOrders > 0 ? (totalBackordered / totalOrders) * 100 : 0;
    
    
    const relevantCountries = filteredData.filter(item => item.totalOrders >= 10);
    let highestMisPickRate = { country: '-', rate: 0 };
    
    if (relevantCountries.length > 0) {
      highestMisPickRate = relevantCountries.reduce((prev, current) => 
        (current.misPickRate > prev.rate) 
          ? { country: current.country, rate: current.misPickRate } 
          : prev
      , { country: '-', rate: 0 });
    }

    return {
      totalOrders,
      totalBackordered,
      averageMisPickRate,
      highestMisPickRate
    };
  }, [filteredData]);

  
  const fetchMispickData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      
      const response = await superagent
        .post(`${backendUrl}/orders/calculateMisPickRateByShippingCountry`)
   
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');
      
      
      if (response.body && Array.isArray(response.body)) {
        setReportData(response.body);
        console.log('API response data:', response.body);
      } else {
        console.error('API response format is unexpected:', response.body);
        toast({
          title: "Data Error",
          description: "Incorrect data format from API. Please try again later.",
          variant: "destructive",
        });
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching mispick data:', error);
      toast({
        title: "Connection Error",
        description: "Unable to fetch mispick report data. Please try again later.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  });

  
  useEffect(() => {
    fetchMispickData();
  }, []);

  
  useEffect(() => {
    let filtered = [...reportData];

  
    if (selectedCountries.length > 0) {
      filtered = filtered.filter(item => selectedCountries.includes(item.country));
    }

    
    filtered = filtered.sort((a, b) => b.misPickRate - a.misPickRate);

    setFilteredData(filtered);
  }, [reportData, selectedCountries]);

  
  const countryOptions = React.useMemo(() => {
    return reportData
      .map(item => item.country)
      .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
      .sort();
  }, [reportData]);

  
  const barChartData = React.useMemo(() => {
    const top10Countries = [...filteredData]
      .filter(item => item.misPickRate > 0)
      .sort((a, b) => b.misPickRate - a.misPickRate)
      .slice(0, 10);

    return {
      labels: top10Countries.map(item => item.country),
      datasets: [
        {
          label: 'Mispick Rate (%)',
          data: top10Countries.map(item => item.misPickRate),
          backgroundColor: COLORS[0],
          borderColor: COLORS[0],
          borderWidth: 1,
        }
      ]
    };
  }, [filteredData]);

  
  const pieChartData = React.useMemo(() => {
    
    const significantCountries = filteredData
      .filter(item => item.misPickRate > 0 && item.totalOrders >= 10)
      .sort((a, b) => b.backordered - a.backordered);
    

    const topCountries = significantCountries.slice(0, 5);
    
    
    const otherCountries = significantCountries.slice(5);
    const otherBackordered = otherCountries.reduce((sum, item) => sum + item.backordered, 0);
    
    const chartData = [...topCountries];
    if (otherBackordered > 0) {
      chartData.push({
        country: 'Others',
        backordered: otherBackordered
      });
    }

    return {
      labels: chartData.map(item => item.country),
      datasets: [
        {
          data: chartData.map(item => item.backordered),
          backgroundColor: COLORS.slice(0, chartData.length),
          borderWidth: 1,
        }
      ]
    };
  }, [filteredData]);

  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Mispick Rate (%)'
        }
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed || context.parsed.y || 0;
            return `${context.dataset.label || 'Mispick Rate'}: ${value.y}%`;
          }
        }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const dataset = context.dataset;
            const total = dataset.data.reduce((acc, current) => acc + current, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} orders (${percentage}%)`;
          }
        }
      }
    }
  };

  
  const exportToCsv = () => {
    if (filteredData.length === 0) {
      toast({
        title: "No Data",
        description: "No data available to export.",
        variant: "warning",
      });
      return;
    }

    
    const headers = ['Country', 'Total Orders', 'Mispick Orders', 'Mispick Rate (%)'];
    const csvContent = [
      headers.join(','),
      ...filteredData.map(item => [
        `"${item.country}"`,
        item.totalOrders,
        item.backordered,
        item.misPickRate.toFixed(2)
      ].join(','))
    ].join('\n');

    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `mispick-report-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    toast({
      title: "Export Successful",
      description: "CSV report has been downloaded successfully.",
    });
  };

  const handleMultiCountryChange = (value) => {
    if (selectedCountries.includes(value)) {
      setSelectedCountries(selectedCountries.filter(country => country !== value));
    } else {
      setSelectedCountries([...selectedCountries, value]);
    }
  };

  return (
  
    <div className="container mx-auto">
      <div className="w-full">
      <CardHeader className="pt-0 pl-0">
            <CardTitle className="text-2xl font-bold">Mispick Rate Report</CardTitle>
            <CardDescription>
              Monitor and analyze mispick rates by country and location
            </CardDescription>
          </CardHeader>
        <Card className="shadow-sm">
          
          
          <CardContent>
            {/* Filters */}
            <Card className="bg-muted/50 my-6">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 gap-4 items-end">
                  
                  <div className="md:col-span-4">
                    <Label>Country</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {countryOptions.length === 0 ? (
                        <div className="text-muted-foreground italic">No country data available</div>
                      ) : (
                        <>
                          {countryOptions.map(country => (
                            <Badge 
                              key={country}
                              variant={selectedCountries.includes(country) ? "default" : "outline"}
                              className="cursor-pointer"
                              onClick={() => handleMultiCountryChange(country)}
                            >
                              {country}
                            </Badge>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                
                </div>
              </CardContent>
            </Card>
            
            {/* Summary statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-2">
                    <Package className="h-6 w-6 text-primary" />
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        Total Orders
                      </div>
                      <div className="text-2xl font-bold">
                        {isLoading ? <Skeleton className="h-8 w-20" /> : summaryData.totalOrders.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-6 w-6 text-destructive" />
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        Mispick Orders
                      </div>
                      <div className="text-2xl font-bold">
                        {isLoading ? <Skeleton className="h-8 w-20" /> : summaryData.totalBackordered.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">
                      Average Mispick Rate
                    </div>
                    <div className={`text-2xl font-bold ${
                      summaryData.averageMisPickRate > 1 ? 'text-destructive' : 
                      summaryData.averageMisPickRate > 0.5 ? 'text-amber-500' : 'text-emerald-600'
                    }`}>
                      {isLoading ? <Skeleton className="h-8 w-20" /> : `${summaryData.averageMisPickRate.toFixed(2)}%`}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">
                      Country with Highest Rate
                    </div>
                    <div className="text-xl font-semibold truncate">
                      {isLoading ? (
                        <Skeleton className="h-8 w-full" />
                      ) : (
                        `${summaryData.highestMisPickRate.country} (${summaryData.highestMisPickRate.rate.toFixed(2)}%)`
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
              <Card className="lg:col-span-8">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-md font-medium">
                    Top Countries with Highest Mispick Rate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="flex items-center justify-center h-[300px]">
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
                        <div className="text-sm text-muted-foreground">Loading data...</div>
                      </div>
                    </div>
                  ) : barChartData.labels.length > 0 ? (
                    <div className="h-[300px]">
                      <Bar data={barChartData} options={chartOptions} />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-[300px] border rounded-md border-dashed">
                      <div className="text-center text-muted-foreground">
                        <div className="text-2xl mb-2">📊</div>
                        <div>No data to display</div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card className="lg:col-span-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-md font-medium">
                    Mispick Distribution by Country
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="flex items-center justify-center h-[300px]">
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
                        <div className="text-sm text-muted-foreground">Loading data...</div>
                      </div>
                    </div>
                  ) : pieChartData.labels.length > 0 ? (
                    <div className="h-[300px]">
                      <Pie data={pieChartData} options={pieChartOptions} />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-[300px] border rounded-md border-dashed">
                      <div className="text-center text-muted-foreground">
                        <div className="text-2xl mb-2">🥧</div>
                        <div>No data to display</div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Detailed data table */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-md font-medium">
                  Detailed Data by Country
                </CardTitle>
                <div className="flex gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="outline" 
                          onClick={exportToCsv}
                          disabled={filteredData.length === 0}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Export CSV
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Export data to CSV file</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : filteredData.length > 0 ? (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Country</TableHead>
                          <TableHead className="text-right">Total Orders</TableHead>
                          <TableHead className="text-right">Mispick Orders</TableHead>
                          <TableHead className="text-right">Mispick Rate (%)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredData.map((item) => (
                          <TableRow key={item.country}>
                            <TableCell className="font-medium">{item.country}</TableCell>
                            <TableCell className="text-right">{item.totalOrders.toLocaleString()}</TableCell>
                            <TableCell className="text-right">{item.backordered.toLocaleString()}</TableCell>
                            <TableCell 
                              className={`text-right font-medium ${
                                item.misPickRate > 1 ? 'text-destructive' : 
                                item.misPickRate > 0.5 ? 'text-amber-500' : ''
                              }`}
                            >
                              {item.misPickRate.toFixed(2)}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell>Total</TableCell>
                          <TableCell className="text-right">{summaryData.totalOrders.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{summaryData.totalBackordered.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{summaryData.averageMisPickRate.toFixed(2)}%</TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                ) : (
                  <div className="flex items-center justify-center p-8 border rounded-md border-dashed">
                    <div className="text-center text-muted-foreground">
                      <div className="text-2xl mb-2">📋</div>
                      <div>No data to display</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </div>
    
  );
};

export default MispickReportPage;
